import { inject, injectable } from 'tsyringe';
import { type IBuyerUserRepository } from 'application/repositorySchemas/IBuyerUserRepository';
import { type IBuyerUserTokenRepository } from 'application/repositorySchemas/IBuyerUserTokenRepository';
import { type IBuyerLoginByEmailClient } from 'interfaceAdapter/gatewaySchemas/IBuyerLoginByEmailClient';
import { type IBuyerUserLogoutClient } from 'interfaceAdapter/gatewaySchemas/IBuyerUserLogoutClient';
import {
    type IBuyerUserProfileRegistrationClient,
    IPostInput as IProfileRegistrationInput,
} from 'interfaceAdapter/gatewaySchemas/IBuyerUserProfileRegistrationClient';
import {
    type IEditBuyerUserProfileClient,
    IPostInput as IEditProfileInput,
} from 'interfaceAdapter/gatewaySchemas/IEditBuyerUserProfileClient';
import { type IBuyerSendPasswordResetEmailClient } from 'interfaceAdapter/gatewaySchemas/IBuyerSendPasswordResetEmailClient';
import { type IBuyerUserPasswordClient } from 'interfaceAdapter/gatewaySchemas/IBuyerUserPasswordClient';

@injectable()
export class BuyerUserRepository implements IBuyerUserRepository {
    public readonly token: string;

    constructor(
        @inject('IBuyerUserTokenRepository')
        public readonly buyerUserToken: IBuyerUserTokenRepository,
        @inject('IBuyerLoginByEmailClient')
        public readonly buyerLoginByEmail: IBuyerLoginByEmailClient,
        @inject('IBuyerUserLogoutClient')
        public readonly buyerUserLogoutClient: IBuyerUserLogoutClient,
        @inject('IBuyerUserProfileRegistrationClient')
        public readonly buyerUserProfileRegistration: IBuyerUserProfileRegistrationClient,
        @inject('IEditBuyerUserProfileClient')
        public readonly editBuyerUserProfile: IEditBuyerUserProfileClient,
        @inject('IBuyerSendPasswordResetEmailClient')
        public readonly buyerSendPasswordResetEmailClient: IBuyerSendPasswordResetEmailClient,
        @inject('IBuyerUserPasswordResetClient')
        public readonly buyerUserPasswordResetClient: IBuyerUserPasswordClient,
    ) {
        this.token = this.buyerUserToken.get() ?? '';
    }

    public loginByEmail = async (email: string, password: string) => {
        const { data, message } = await this.buyerLoginByEmail.post({ email, password });
        if (message) {
            throw new Error(message);
        }
        this.buyerUserToken.remove();
        this.buyerUserToken.set(data.api_token);
        return data;
    };

    public logout = async () => {
        await this.buyerUserLogoutClient.post({ token: this.token });
    };

    public registerProfile = async (buyer_user: IProfileRegistrationInput['buyer_user']) => {
        const { data, message } = await this.buyerUserProfileRegistration.post({
            token: this.buyerUserToken.get() || '',
            buyer_user,
        });

        if (message) {
            throw new Error(message);
        }

        return data;
    };

    public editProfile = async (buyer_user: IEditProfileInput['buyer_user']) => {
        const { data, message } = await this.editBuyerUserProfile.post({
            token: this.buyerUserToken.get() || '',
            buyer_user,
        });

        if (message) {
            throw new Error(message);
        }

        return data;
    };

    public sendResetPasswordEmail = async (email: string) => {
        const { message } = await this.buyerSendPasswordResetEmailClient.post({ email });
        if (message) {
            throw new Error(message);
        }
    };

    public async resetPassword(code: string, password: string) {
        const { data, message } = await this.buyerUserPasswordResetClient.post({
            code,
            password,
        });

        if (message) {
            throw new Error(message);
        }

        return { result: data };
    }
}
